import { Basket, BasketCampaignData, BasketResponseModel } from '@/models/Basket';
import { CheckoutState } from '@/models/CheckoutState';
import { GenericResponse } from '@/models/GenericResponse';
import { CheckoutFormField } from './CheckoutFormField';
import {
  getBasketForApp,
  getCampaignDataForApp,
  setBasketForApp,
  setCampaignDataForApp,
} from './CookieUtils';
import { decryptAES, encryptAES } from './crypto/AES';
import { encryptMD5 } from './crypto/MD5';
import { Endpoints } from './Endpoints';
import { readMigratedItem, removeMigratedItem, writeMigratedItem } from './crypto/storageMigration';
import { formatPhoneNumber } from './PhoneUtils';
import { Requester } from './Requester';
import { safeSessionStorage } from './safeStorage';
import { STORAGE_KEY } from './storageKeys';
import { appRPC } from './utils';
import { WindowDispatchEvent } from './WindowDispatchEvent';

const isBrowser = () => typeof window !== 'undefined';

const safeLocalStorageGet = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeLocalStorageSet = (key: string, value: string): void => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // ignore quota / security errors
  }
};

const safeLocalStorageRemove = (key: string): void => {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // ignore
  }
};

const lsStore = {
  getItem: safeLocalStorageGet,
  setItem: (key: string, value: string) => safeLocalStorageSet(key, value),
  removeItem: safeLocalStorageRemove,
};

export const getCampaignData = (): BasketCampaignData | undefined => {
  if (!isBrowser()) return undefined;

  try {
    const campaignDataValue = readMigratedItem(lsStore, STORAGE_KEY.CAMPAIGN_DATA);
    if (campaignDataValue) {
      const decrypted = decryptAES(campaignDataValue);
      const parsed = JSON.parse(decrypted) as BasketCampaignData;
      return parsed;
    }
  } catch (e) {}

  try {
    return getCampaignDataForApp(encryptMD5(STORAGE_KEY.CAMPAIGN_DATA));
  } catch (e) {
    return undefined;
  }
};

export const getBasket = (): Basket | undefined => {
  if (!isBrowser()) return undefined;

  try {
    const basketValue = readMigratedItem(lsStore, STORAGE_KEY.BASKET);
    if (basketValue) {
      const decrypted = decryptAES(basketValue);
      const parsed = JSON.parse(decrypted) as Basket;
      return parsed;
    }
  } catch (e) {
    // localStorage / decrypt / JSON parse hataları olursa app cookie fallback'e düş
  }

  try {
    return getBasketForApp(encryptMD5(STORAGE_KEY.BASKET));
  } catch (e) {
    return undefined;
  }
};

export const extractFormFieldsFromBasket = (basket: any): { [key: string]: string } => {
  const formFields: { [key: string]: string } = {};

  if (basket.receiverInformation) {
    const receiver = basket.receiverInformation;
    if (receiver.title) formFields[CheckoutFormField.RECEIVER_FULL_NAME] = receiver.title;
    if (receiver.phoneNumber)
      formFields[CheckoutFormField.RECEIVER_PHONE] = formatPhoneNumber(receiver.phoneNumber);
    if (receiver.city) formFields[CheckoutFormField.RECEIVER_CITY] = receiver.city;
    if (receiver.district) formFields[CheckoutFormField.RECEIVER_DISTRICT] = receiver.district;
    if (receiver.neighborhood)
      formFields[CheckoutFormField.RECEIVER_NEIGHBORHOOD] = receiver.neighborhood;
    if (receiver.address) formFields[CheckoutFormField.RECEIVER_ADDRESS] = receiver.address;
  }

  if (basket.senderInformation) {
    const sender = basket.senderInformation;
    if (sender.title) formFields[CheckoutFormField.SENDER_FULL_NAME] = sender.title;
    if (sender.phoneNumber)
      formFields[CheckoutFormField.SENDER_PHONE] = formatPhoneNumber(sender.phoneNumber);
    if (sender.email) formFields[CheckoutFormField.SENDER_MAIL] = sender.email;
  }

  if (basket.invoiceInformation) {
    const invoice = basket.invoiceInformation;
    formFields[CheckoutFormField.INVOICE_REQUESTED] = invoice.invoiceRequested ? 'true' : 'false';
    if (invoice.companyNamedInvoice !== undefined) {
      formFields[CheckoutFormField.COMPANY_NAMED_INVOICE] = invoice.companyNamedInvoice
        ? 'true'
        : 'false';
    }
    if (invoice.invoiceTitle) formFields[CheckoutFormField.INVOICE_TITLE] = invoice.invoiceTitle;
    if (invoice.taxOffice) formFields[CheckoutFormField.TAX_OFFICE] = invoice.taxOffice;
    if (invoice.taxNumber) formFields[CheckoutFormField.TAX_NUMBER] = invoice.taxNumber;
    if (invoice.invoiceAddress)
      formFields[CheckoutFormField.INVOICE_ADDRESS] = invoice.invoiceAddress;
  }

  return formFields;
};

async function getBasketResponse(): Promise<any | null> {
  try {
    const { data: result } = await Requester.get<GenericResponse<BasketResponseModel>>(
      Endpoints.GET_BASKET,
    );
    return result ?? null;
  } catch {
    return null;
  }
}

export const fetchFormFieldsFromAPI = async (): Promise<{ [key: string]: string } | null> => {
  try {
    const result = await getBasketResponse();
    if (result) {
      const apiBasketRaw = result?.data?.basket ?? result?.basket ?? result?.data ?? result;

      if (apiBasketRaw && typeof apiBasketRaw === 'object') {
        const extractedFields = extractFormFieldsFromBasket(apiBasketRaw);
        return extractedFields;
      }
    }

    return null;
  } catch (error) {
    console.error("API'den form verisi çekme hatası:", error);
    return null;
  }
};

let inFlight: Promise<Basket | undefined> | null = null;

export async function refreshBasketFromAPI(setFrom?: string): Promise<Basket | undefined> {
  if (inFlight) return inFlight;

  inFlight = (async () => {
    const tag = `BasketStorage - refreshBasketFromAPI${setFrom ? ` - ${setFrom}` : ''}`;

    try {
      const result = await getBasketResponse();
      if (!result) {
        const local = getBasket();
        setCampaignData(undefined, `${tag} http_fail no_campaign`);
        if (local && Array.isArray((local as any).items)) {
          setBasket(local, `${tag} http_fail`);
          return local;
        }

        setBasket(undefined, `${tag} http_fail no_local`);
        return undefined;
      }

      setCampaignData(result?.data?.basket, `${tag} success`);

      const apiBasketRaw = result?.data?.basket ?? result?.basket ?? result?.data ?? result;

      if (apiBasketRaw == null) {
        setBasket(undefined, `${tag} api_null`);
        return undefined;
      }

      const items = (apiBasketRaw as any).items;
      if (!Array.isArray(items)) {
        setBasket(undefined, `${tag} invalid_shape`);
        return undefined;
      }

      const basket = apiBasketRaw as Basket;

      setBasket(basket, `${tag} success`);
      return basket;
    } catch (err) {
      const local = getBasket();
      if (local && Array.isArray((local as any).items)) {
        setBasket(local, `${tag} error_local_fallback`);
        return local;
      }

      setBasket(undefined, `${tag} error_no_local`);
      return undefined;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

export const setCampaignData = (campaignData?: Basket, setFrom?: string) => {
  const campaignDataKey = encryptMD5(STORAGE_KEY.CAMPAIGN_DATA);
  if (!isBrowser()) return;

  if (!campaignData) {
    removeMigratedItem(lsStore, STORAGE_KEY.CAMPAIGN_DATA);
    setCampaignDataForApp(campaignDataKey, undefined);
  } else {
    const encryptedCampaignData = encryptAES(JSON.stringify(campaignData));
    writeMigratedItem(lsStore, STORAGE_KEY.CAMPAIGN_DATA, encryptedCampaignData);
    setCampaignDataForApp(campaignDataKey, encryptedCampaignData);
  }
};

let lastSerializedBasket: string | null = null;

export const setBasket = (basket?: Basket, setFrom?: string) => {
  const basketKey = encryptMD5(STORAGE_KEY.BASKET);
  if (!isBrowser()) return;

  const items = (basket as any)?.items;
  if (basket !== undefined) {
    const invalidShape = !Array.isArray(items);
    if (invalidShape) {
      basket = undefined;
    } else {
      // items: [] => geçerli ama boş sepet. Checkout'ta uygunluk kontrolü yapılmalı.
      // Boş sepette eski checkout state kalmasın.
      if (Array.isArray(items) && items.length === 0) {
        setCheckoutState(undefined);
      }
    }
  }

  if (basket === undefined) {
    lastSerializedBasket = null;

    removeMigratedItem(lsStore, STORAGE_KEY.BASKET);
    setBasketForApp(basketKey, undefined);
    setCheckoutState(undefined);

    window.dispatchEvent(
      new CustomEvent(WindowDispatchEvent.UPDATE_BASKET, {
        detail: { from: setFrom, reason: 'reset' },
      }),
    );

    appRPC('bridge', 'updateBasketBadge', { itemCount: 0, setFrom: setFrom || 'local' });
    return;
  }

  const serialized = JSON.stringify({
    orderNumber: basket.orderNumber ?? '',
    total: basket.total ?? 0,
    deliveryFee: basket.deliveryFee ?? 0,
    discount: basket.discount ?? 0,
    deliveryTimeId: basket.deliveryTime?.id ?? '',
    deliveryDate: basket.deliveryTime?.date ?? '',
    districtId: basket.district?.id ?? '',
    showAppPromoBanner: basket.showAppPromoBanner ?? 0,
    hasMobileDevice: basket.userCampaignData?.hasMobileDevice ? 1 : 0,
    hasMobilePurchase: basket.userCampaignData?.hasMobilePurchase ? 1 : 0,
    items: (basket.items ?? []).map((i) => `${i.id}:${i.count}:${i.isExtra ? 1 : 0}`).join('|'),
    milesIssuedAt: basket.paymentInformation?.miles?.issuedAt ?? '',
    milesQty: basket.paymentInformation?.miles?.quantity ?? 0,
    cardAmount: basket.paymentInformation?.card?.amount ?? '',
  });

  if (serialized === lastSerializedBasket) {
    return;
  }

  lastSerializedBasket = serialized;

  const encryptedBasket = encryptAES(JSON.stringify(basket));
  writeMigratedItem(lsStore, STORAGE_KEY.BASKET, encryptedBasket);
  setBasketForApp(basketKey, encryptedBasket);

  window.dispatchEvent(
    new CustomEvent(WindowDispatchEvent.UPDATE_BASKET, {
      detail: { from: setFrom, reason: 'update' },
    }),
  );

  const basketItemCount = basket.items?.reduce((sum, item) => sum + item.count, 0) || 0;

  appRPC('bridge', 'updateBasketBadge', {
    itemCount: basketItemCount,
    count: basketItemCount,
    setFrom: setFrom || 'local',
  });
};

export const syncBasketWithAPI = async (): Promise<boolean> => {
  try {
    const result = await getBasketResponse();
    if (result) {
      setCampaignData(result?.data?.basket, 'BasketStorage - syncBasketWithAPI');
      const apiBasketRaw: any = result?.data?.basket ?? result?.basket ?? result?.data ?? result;
      const localBasket = getBasket();

      const apiItems = apiBasketRaw?.items;
      const localItems = (localBasket as any)?.items;

      const apiHasValidItemsArray = Array.isArray(apiItems);
      const localHasValidItemsArray = Array.isArray(localItems);

      if (apiHasValidItemsArray && apiItems.length > 0) {
        setBasket(apiBasketRaw as Basket);
        return true;
      } else if (localHasValidItemsArray && localItems.length > 0) {
        // API boş/invalid ise local'de ürün varsa, server gerçeğini baz alıp local'i sıfırla
        setBasket(undefined);
        setCampaignData(undefined, 'BasketStorage - syncBasketWithAPI - clear_local');
        return true;
      }

      return false;
    }

    throw new Error('API yanıtı başarısız');
  } catch (error) {
    console.error('Sepet senkronizasyonu başarısız:', error);
    return false;
  }
};

export const initBasketSync = () => {
  if (!isBrowser()) return;

  const syncKey = 'basket_synced_' + new Date().toDateString();
  if (safeSessionStorage.getItem(syncKey)) return;

  syncBasketWithAPI().then((synced) => {
    if (synced) {
      safeSessionStorage.setItem(syncKey, 'true');
    }
  });
};

export const getCheckoutState = (): CheckoutState | undefined => {
  if (!isBrowser()) return undefined;
  try {
    const checkoutStateValue = readMigratedItem(lsStore, STORAGE_KEY.CHECKOUT_STATE);
    if (!checkoutStateValue) return undefined;
    const decrypted = decryptAES(checkoutStateValue);
    if (!decrypted) return undefined;
    return JSON.parse(decrypted);
  } catch {
    return undefined;
  }
};

export const setCheckoutState = (checkoutState?: CheckoutState) => {
  if (!isBrowser()) return;

  if (!checkoutState) {
    removeMigratedItem(lsStore, STORAGE_KEY.CHECKOUT_STATE);
    return;
  }

  writeMigratedItem(lsStore, STORAGE_KEY.CHECKOUT_STATE, encryptAES(JSON.stringify(checkoutState)));
};

const PENDING_ORDER_NUMBER_KEY = 'hzc_pending_order_number';

export const rememberPendingOrderNumber = (orderNumber?: string | null) => {
  if (!orderNumber) return;
  safeSessionStorage.setItem(PENDING_ORDER_NUMBER_KEY, orderNumber);
};

export const getPendingOrderNumber = (): string | undefined =>
  safeSessionStorage.getItem(PENDING_ORDER_NUMBER_KEY) || undefined;

export const clearPendingOrderNumber = () => {
  safeSessionStorage.removeItem(PENDING_ORDER_NUMBER_KEY);
};

export const getLastOrderId = (): string | undefined => {
  if (!isBrowser()) return undefined;
  const lastOrderIdValue = readMigratedItem(lsStore, STORAGE_KEY.LAST_ORDER_ID);
  return lastOrderIdValue ? decryptAES(lastOrderIdValue) : undefined;
};

export const setLastOrderId = (orderId: string | undefined) => {
  if (!isBrowser()) return;

  if (!orderId) {
    removeMigratedItem(lsStore, STORAGE_KEY.LAST_ORDER_ID);
    return;
  }

  writeMigratedItem(lsStore, STORAGE_KEY.LAST_ORDER_ID, encryptAES(orderId));
};

export const getLastPaymentType = (): string | undefined => {
  if (!isBrowser()) return undefined;
  const lastPaymentTypeValue = readMigratedItem(lsStore, STORAGE_KEY.LAST_PAYMENT_TYPE);
  return lastPaymentTypeValue ? decryptAES(lastPaymentTypeValue) : undefined;
};

export const setLastPaymentType = (paymentType: string | undefined) => {
  if (!isBrowser()) return;

  if (!paymentType) {
    removeMigratedItem(lsStore, STORAGE_KEY.LAST_PAYMENT_TYPE);
    return;
  }

  writeMigratedItem(lsStore, STORAGE_KEY.LAST_PAYMENT_TYPE, encryptAES(paymentType));
};
